import { useLocation, Link } from "react-router-dom";

// Language detection from URL path
function getLangFromPath(pathname: string): "de" | "en" | "fr" | "es" {
  const seg = pathname.split("/")[1];
  if (["de", "en", "fr", "es"].includes(seg)) return seg as "de" | "en" | "fr" | "es";
  return "de"; // fallback
}

const texts: Record<string, {
  title: string;
  message: string;
  homeLabel: string;
  homeHref: string;
}> = {
  de: {
    title: "Seite nicht gefunden",
    message: "Die angeforderte Seite existiert leider nicht. Möglicherweise wurde sie verschoben oder die URL enthält einen Tippfehler.",
    homeLabel: "Zur Startseite",
    homeHref: "/de",
  },
  en: {
    title: "Page Not Found",
    message: "The requested page does not exist. It may have been moved or the URL may contain a typo.",
    homeLabel: "Back to Home",
    homeHref: "/en",
  },
  fr: {
    title: "Page introuvable",
    message: "La page demandée n'existe malheureusement pas. Elle a peut-être été déplacée ou l'URL contient une erreur de saisie.",
    homeLabel: "Retour à l'accueil",
    homeHref: "/fr",
  },
  es: {
    title: "Página no encontrada",
    message: "La página solicitada no existe. Es posible que haya sido movida o que la URL contenga un error tipográfico.",
    homeLabel: "Volver al inicio",
    homeHref: "/es",
  },
};

export default function NotFound() {
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const t = texts[lang];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 px-4">
      {/* Background 404 watermark */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10rem] md:text-[14rem] font-black text-foreground-50 select-none pointer-events-none z-0 whitespace-nowrap leading-none" aria-hidden="true">
        404
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-lg mx-auto">
        {/* Icon */}
        <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-6">
          <i className="ri-error-warning-line text-5xl md:text-6xl text-secondary-500"></i>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
          {t.title}
        </h1>

        {/* Requested path */}
        <p className="text-sm font-mono text-foreground-500 bg-background-100 rounded-md px-3 py-1.5 mb-4 break-all">
          {location.pathname}
        </p>

        {/* Message */}
        <p className="text-base text-foreground-600 leading-relaxed mb-8 max-w-md">
          {t.message}
        </p>

        {/* Home button */}
        <Link
          to={t.homeHref}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-heading font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-4-line text-lg"></i>
          {t.homeLabel}
        </Link>

        {/* Sitemap link hint */}
        <p className="mt-8 text-xs text-foreground-400">
          {lang === "de" && "Sie können auch unsere"}
          {lang === "en" && "You can also browse our"}
          {lang === "fr" && "Vous pouvez également parcourir notre"}
          {lang === "es" && "También puede explorar nuestra"}
          {" "}
          <Link to={`/${lang}/services`} className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
            {lang === "de" && "Services-Übersicht"}
            {lang === "en" && "Services Overview"}
            {lang === "fr" && "aperçu des services"}
            {lang === "es" && "página de servicios"}
          </Link>
          {" "}
          {lang === "de" && "durchstöbern."}
          {lang === "en" && "."}
          {lang === "fr" && "."}
          {lang === "es" && "."}
        </p>
      </div>
    </div>
  );
}